// Asset preview — MECHS tab (DESIGN §9 step 8): every animation of the selected
// mech at gameplay size, both directions, looping, with a full loadout; preset
// builds, per-slot cycling, attachment / foot-anchor overlays, 2× zoom, slow-mo,
// frame stepping and sheet export.
//
// Animation is driven by the preview's own clock through MechViewApi.setPhase so
// every cell can be slowed, paused and stepped deterministically. When the
// MechView module is unavailable the raw sheet frames from art.mechs are shown.

import Phaser from 'phaser';
import { art } from '../../art/registry';
import { CELL, FOOT_ANCHOR } from '../../config/constants';
import { COLORS, css } from '../../config/palette';
import type { MechViewApi } from '../../core/contracts';
import {
  ANIM_FRAMES,
  ANIM_KEYS,
  ATTACH_KEYS,
  EQUIP_IDS,
  EQUIP_SLOTS,
  EQUIP_SLOT_OF,
  MECH_IDS,
  WEAPON_IDS,
  type AnimKey,
  type AttachKey,
  type EquipId,
  type EquipSlot,
  type MechArt,
  type MechFrameMeta,
  type MechId,
  type WeaponId,
} from '../../core/types';
import { MECHS } from '../../data/mechs';
import { createMechView } from '../../systems/player/MechView';
import { downloadJson, downloadTexture } from './exporter';
import type { PreviewTab } from './tab';
import { Button, Cycler, PV, drawPanel, idLabel, label, sectionTitle } from './widgets';

type Loadout = { weapon: WeaponId | null; equip: Record<EquipSlot, EquipId | null> };

interface Preset {
  name: string;
  build: (mech: MechId) => Loadout;
}

const emptyEquip = (): Record<EquipSlot, EquipId | null> => ({ chest: null, shoulders: null, back: null, legs: null });

const PRESETS: Preset[] = [
  { name: 'BARE', build: (m) => ({ weapon: MECHS[m].startingWeapon, equip: emptyEquip() }) },
  {
    name: 'STORM RUNNER',
    build: () => ({
      weapon: 'arc_lance',
      equip: { chest: 'reactive_plating', shoulders: 'storm_capacitor', back: 'tempest_reactor', legs: 'vector_boosters' },
    }),
  },
  {
    name: 'WALKING FORTRESS',
    build: () => ({
      weapon: 'rail_driver',
      equip: { chest: 'reactive_plating', shoulders: 'aegis_pauldrons', back: 'overdrive_reactor', legs: 'anchor_greaves' },
    }),
  },
  {
    name: 'GRAVITY FURNACE',
    build: () => ({
      weapon: 'gravity_launcher',
      equip: { chest: 'capacitor_core', shoulders: 'missile_pods', back: 'heat_sink_array', legs: 'cinder_treads' },
    }),
  },
  {
    name: 'RANDOM',
    // Cosmetic preview only: Math.random is fine here.
    build: () => {
      const pick = <T>(xs: readonly T[]): T => xs[Math.floor(Math.random() * xs.length)];
      const equip = emptyEquip();
      for (const slot of EQUIP_SLOTS) equip[slot] = pick(EQUIP_IDS.filter((id) => EQUIP_SLOT_OF[id] === slot));
      return { weapon: pick(WEAPON_IDS), equip };
    },
  },
];

/** Short labels for the attachment overlay. */
const ATTACH_SHORT: Record<AttachKey, string> = {
  head: 'hd',
  chest: 'ch',
  back: 'bk',
  shoulderF: 'sF',
  shoulderR: 'sR',
  hip: 'hip',
  shinF: 'nF',
  shinR: 'nR',
  footF: 'fF',
  footR: 'fR',
};
const ATTACH_COLOR: Record<AttachKey, number> = {
  head: 0xffffff,
  chest: COLORS.orbArmor,
  back: COLORS.burn,
  shoulderF: COLORS.orbArsenal,
  shoulderR: 0xb59a2a,
  hip: COLORS.shield,
  shinF: COLORS.electric,
  shinR: 0x4aa2a8,
  footF: COLORS.orbUnstable,
  footR: 0xa4329c,
};

/** How long a non-looping animation holds its last frame before replaying. */
const HOLD_SEC: Partial<Record<AnimKey, number>> = { death: 1.0, hurt: 0.35, signature: 0.25 };

// ───────────────────────────── Cell views ─────────────────────────────

interface CellView {
  readonly real: boolean;
  readonly root: Phaser.GameObjects.Container;
  show(anim: AnimKey, frame: number, count: number, dt: number): void;
  setLoadout(l: Loadout): void;
  setAim(angle: number): void;
  recoil(): void;
  destroy(): void;
}

/** The real layered renderer (player module). */
class RealView implements CellView {
  readonly real = true;
  readonly root: Phaser.GameObjects.Container;

  constructor(
    private view: MechViewApi,
    mech: MechId,
    x: number,
    feetY: number,
    facing: 1 | -1,
    scale: number,
  ) {
    view.setScale(scale);
    view.setFacing(facing);
    view.setPosition(x, feetY);
    view.setReactorColor(MECHS[mech].reactorColor);
    view.setAim(facing === 1 ? 0 : Math.PI);
    this.root = view.root;
  }

  show(anim: AnimKey, frame: number, count: number, dt: number): void {
    this.view.setPhase(anim, Math.min(0.999, (frame + 0.5) / count));
    this.view.update(dt);
  }

  setLoadout(l: Loadout): void {
    this.view.setWeapon(l.weapon);
    this.view.setEquipment(EQUIP_SLOTS.map((s) => l.equip[s]).filter((v): v is EquipId => !!v));
  }

  setAim(angle: number): void {
    this.view.setAim(angle);
  }

  recoil(): void {
    this.view.recoil(1);
  }

  destroy(): void {
    try {
      this.view.destroy();
    } catch {
      this.root.destroy();
    }
  }
}

/** Raw sheet frames (preview composite if available, else body + additive glow). */
class SheetView implements CellView {
  readonly real = false;
  readonly root: Phaser.GameObjects.Container;
  private body: Phaser.GameObjects.Image | null = null;
  private glow: Phaser.GameObjects.Image | null = null;

  constructor(
    scene: Phaser.Scene,
    private mechArt: MechArt | undefined,
    mech: MechId,
    x: number,
    feetY: number,
    facing: 1 | -1,
    scale: number,
  ) {
    this.root = scene.add.container(x, feetY).setScale(scale * facing, scale);
    const a = mechArt;
    const cell = a?.cell ?? CELL;
    const fa = a?.footAnchor ?? FOOT_ANCHOR;
    const usePreview = !!a?.previewSheetKey && scene.textures.exists(a.previewSheetKey);
    const bodyKey = usePreview ? a!.previewSheetKey! : a?.sheetKey;
    if (a && bodyKey && scene.textures.exists(bodyKey)) {
      this.body = scene.add.image(0, 0, bodyKey, a.anims.idle?.start ?? 0).setOrigin(fa.x / cell, fa.y / cell);
      this.root.add(this.body);
      if (!usePreview && scene.textures.exists(a.glowSheetKey)) {
        this.glow = scene.add
          .image(0, 0, a.glowSheetKey, a.anims.idle?.start ?? 0)
          .setOrigin(fa.x / cell, fa.y / cell)
          .setTint(MECHS[mech].reactorColor)
          .setBlendMode(Phaser.BlendModes.ADD);
        this.root.add(this.glow);
      }
    } else {
      // No art registered at all: a collider-sized silhouette so layout still reads.
      const g = scene.add.graphics();
      g.fillStyle(COLORS.steel2, 1);
      g.fillRoundedRect(-20, -88, 40, 88, 6);
      g.lineStyle(2, MECHS[mech].reactorColor, 0.9);
      g.strokeRoundedRect(-20, -88, 40, 88, 6);
      g.fillStyle(MECHS[mech].reactorColor, 1);
      g.fillRect(4, -70, 10, 4);
      this.root.add(g);
    }
  }

  show(anim: AnimKey, frame: number): void {
    const info = this.mechArt?.anims[anim];
    if (!info) return;
    const f = info.start + Math.min(frame, info.count - 1);
    if (this.body && this.body.texture.has(String(f))) this.body.setFrame(f);
    if (this.glow && this.glow.texture.has(String(f))) this.glow.setFrame(f);
  }

  setLoadout(): void {}
  setAim(): void {}
  recoil(): void {}

  destroy(): void {
    this.root.destroy();
  }
}

interface MechCell {
  anim: AnimKey;
  facing: 1 | -1;
  x: number;
  feetY: number;
  scale: number;
  view: CellView;
  frame: number;
  lastLoop: number;
}

interface Card {
  anim: AnimKey;
  x: number;
  y: number;
  w: number;
  h: number;
  frameText: Phaser.GameObjects.Text;
  thumbs: { img: Phaser.GameObjects.Image; x: number; y: number; s: number }[];
}

// ───────────────────────────── Tab ─────────────────────────────

export class MechTab implements PreviewTab {
  readonly root: Phaser.GameObjects.Container;
  private stage: Phaser.GameObjects.Container;
  private overlay: Phaser.GameObjects.Graphics;
  private attachLabels: Phaser.GameObjects.Text[] = [];
  private cells: MechCell[] = [];
  private cards: Card[] = [];
  private mechButtons = new Map<MechId, Button>();
  private presetButtons: Button[] = [];
  private toggles: Record<'attach' | 'foot' | 'zoom' | 'slow' | 'step' | 'aim', Button>;
  private weaponCycler!: Cycler<WeaponId | null>;
  private slotCyclers = new Map<EquipSlot, Cycler<EquipId | null>>();
  private status: Phaser.GameObjects.Text;
  private noteText: Phaser.GameObjects.Text;

  private mech: MechId = 'volt';
  private loadout: Loadout = PRESETS[1].build('volt');
  private presetIndex = 1;
  private showAttach = false;
  private showFoot = true;
  private zoom2x = false;
  private slow = false;
  private stepMode = false;
  private aimSweep = false;
  private page = 0;
  private clock = 0;
  private stepIndex = 0;
  private viewWarned = false;

  constructor(private scene: Phaser.Scene) {
    this.root = scene.add.container(0, 0);
    this.stage = scene.add.container(0, 0);
    this.overlay = scene.add.graphics();
    this.root.add(this.buildSidePanel());
    this.root.add(this.buildMechTabs());
    this.root.add(this.stage);
    this.root.add(this.overlay);

    const bx = PV.pad + 16;
    this.toggles = this.buildToggles(bx, 640);
    this.root.add(Object.values(this.toggles).map((b) => b.root));
    this.root.add(this.buildExport(bx, 880));
    this.status = label(scene, bx, 1000, '', { font: 'mono', size: 12, color: COLORS.uiDim });
    this.noteText = label(scene, PV.W - PV.pad, PV.headerH + 18, '', { font: 'mono', size: 12, color: COLORS.uiDim, originX: 1 });
    this.root.add([this.status, this.noteText]);
    this.selectMech('volt');
  }

  // ── construction ──

  private buildSidePanel(): Phaser.GameObjects.GameObject[] {
    const s = this.scene;
    const out: Phaser.GameObjects.GameObject[] = [];
    const g = s.add.graphics();
    drawPanel(g, PV.pad, PV.headerH + 12, PV.sideW, PV.H - PV.headerH - 32, { fill: COLORS.uiPanel, fillAlpha: 0.85, chamfer: 14, accent: COLORS.volt });
    out.push(g);
    const x = PV.pad + 16;
    const w = PV.sideW - 32;

    out.push(...sectionTitle(s, x + 6, PV.headerH + 26, 'BUILD PRESETS', COLORS.volt));
    const py = PV.headerH + 48;
    PRESETS.forEach((p, i) => {
      // Row layout: BARE | RANDOM on the first row, named builds full width below.
      const pos = i === 0 ? { x, y: py, w: w / 2 - 4 } : i === 4 ? { x: x + w / 2 + 4, y: py, w: w / 2 - 4 } : { x, y: py + i * 40, w };
      const b = new Button(s, pos.x, pos.y, p.name, () => this.applyPreset(i), { w: pos.w, h: 34, toggle: true });
      this.presetButtons.push(b);
      out.push(b.root);
    });

    out.push(...sectionTitle(s, x + 6, PV.headerH + 220, 'LOADOUT', COLORS.volt));
    let y = PV.headerH + 242;
    this.weaponCycler = new Cycler<WeaponId | null>(s, x, y, w, 'WEAPON  [W]', [...WEAPON_IDS, null], idLabel, (v) => {
      this.loadout.weapon = v;
      this.loadoutChanged();
    });
    out.push(this.weaponCycler.root);
    for (const slot of EQUIP_SLOTS) {
      y += 58;
      const ids: (EquipId | null)[] = [...EQUIP_IDS.filter((id) => EQUIP_SLOT_OF[id] === slot), null];
      const c = new Cycler<EquipId | null>(s, x, y, w, slot.toUpperCase(), ids, idLabel, (v) => {
        this.loadout.equip[slot] = v;
        this.loadoutChanged();
      });
      this.slotCyclers.set(slot, c);
      out.push(c.root);
    }
    out.push(...sectionTitle(s, x + 6, 614, 'VIEW', COLORS.volt));
    out.push(...sectionTitle(s, x + 6, 854, 'EXPORT', COLORS.volt));
    return out;
  }

  private buildToggles(x: number, y: number): MechTab['toggles'] {
    const s = this.scene;
    const w = (PV.sideW - 32) / 2 - 4;
    const mk = (col: number, row: number, text: string, hint: string, fn: () => void): Button =>
      new Button(s, x + col * (w + 8), y + row * 40, text, fn, { w, h: 34, toggle: true, hint, align: 'left', size: 13 });
    const t = {
      attach: mk(0, 0, 'ATTACH PTS', 'A', () => this.toggle('attach')),
      foot: mk(1, 0, 'FOOT LINE', 'F', () => this.toggle('foot')),
      zoom: mk(0, 1, 'ZOOM 2X', 'Z', () => this.toggle('zoom')),
      slow: mk(1, 1, 'SLOW 0.25X', 'M', () => this.toggle('slow')),
      step: mk(0, 2, 'FRAME STEP', 'P', () => this.toggle('step')),
      aim: mk(1, 2, 'AIM SWEEP', 'G', () => this.toggle('aim')),
    };
    const prev = new Button(s, x, y + 3 * 40, '< FRAME', () => this.stepFrames(-1), { w, h: 34, hint: ',', size: 13 });
    const next = new Button(s, x + w + 8, y + 3 * 40, 'FRAME >', () => this.stepFrames(1), { w, h: 34, hint: '.', size: 13 });
    const pageBtn = new Button(s, x, y + 4 * 40, 'ANIM PAGE (2X)', () => this.flipPage(), { w: w * 2 + 8, h: 34, hint: '[ ]', size: 13 });
    this.root.add([prev.root, next.root, pageBtn.root]);
    return t;
  }

  private buildExport(x: number, y: number): Phaser.GameObjects.GameObject[] {
    const s = this.scene;
    const w = (PV.sideW - 32) / 2 - 4;
    const a = (): MechArt | undefined => art.mechs[this.mech];
    const say = (msg: string): void => {
      this.status.setText(msg);
    };
    const items: [string, () => string][] = [
      ['BODY PNG', () => downloadTexture(s, a()?.sheetKey, `${this.mech}_body.png`)],
      ['GLOW PNG', () => downloadTexture(s, a()?.glowSheetKey, `${this.mech}_glow.png`)],
      ['PREVIEW PNG', () => downloadTexture(s, a()?.previewSheetKey, `${this.mech}_preview.png`)],
      ['META JSON', () => downloadJson(art.sheetsMeta[this.mech], `${this.mech}_sheets.json`)],
    ];
    return items.map(([text, fn], i) => new Button(s, x + (i % 2) * (w + 8), y + Math.floor(i / 2) * 40, text, () => say(fn()), { w, h: 34, size: 13 }).root);
  }

  private buildMechTabs(): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [];
    const x0 = PV.sideW + PV.pad * 2;
    MECH_IDS.forEach((id, i) => {
      const b = new Button(this.scene, x0 + i * 168, PV.headerH + 12, MECHS[id].name, () => this.selectMech(id), {
        w: 160,
        h: 38,
        accent: MECHS[id].reactorColor,
        toggle: true,
        size: 17,
      });
      this.mechButtons.set(id, b);
      out.push(b.root);
    });
    out.push(label(this.scene, x0 + 3 * 168 + 8, PV.headerH + 22, 'Q / E', { font: 'mono', size: 12, color: COLORS.uiDim }));
    return out;
  }

  // ── state changes ──

  private selectMech(id: MechId): void {
    this.mech = id;
    for (const [m, b] of this.mechButtons) b.setActive(m === id);
    const accent = MECHS[id].reactorColor;
    for (const b of this.presetButtons) b.setAccent(accent);
    for (const b of Object.values(this.toggles)) b.setAccent(accent);
    // Keep the chosen preset; "BARE" depends on the mech's starting weapon.
    if (this.presetIndex >= 0 && this.presetIndex !== 4) this.loadout = PRESETS[this.presetIndex].build(id);
    this.syncControls();
    this.rebuildCells();
  }

  private applyPreset(i: number): void {
    this.presetIndex = i;
    this.loadout = PRESETS[i].build(this.mech);
    this.syncControls();
    this.loadoutChanged();
  }

  private loadoutChanged(): void {
    for (const c of this.cells) this.safe(c, () => c.view.setLoadout(this.loadout));
    // A manual change detaches from the preset highlight unless it still matches.
    const matches = PRESETS.findIndex((p, i) => i !== 4 && sameLoadout(p.build(this.mech), this.loadout));
    this.presetIndex = matches >= 0 ? matches : this.presetIndex === 4 ? 4 : -1;
    this.presetButtons.forEach((b, i) => b.setActive(i === this.presetIndex));
  }

  private syncControls(): void {
    this.weaponCycler.set(this.loadout.weapon);
    for (const [slot, c] of this.slotCyclers) c.set(this.loadout.equip[slot]);
    this.presetButtons.forEach((b, i) => b.setActive(i === this.presetIndex));
    this.toggles.attach.setActive(this.showAttach);
    this.toggles.foot.setActive(this.showFoot);
    this.toggles.zoom.setActive(this.zoom2x);
    this.toggles.slow.setActive(this.slow);
    this.toggles.step.setActive(this.stepMode);
    this.toggles.aim.setActive(this.aimSweep);
  }

  private toggle(which: keyof MechTab['toggles']): void {
    switch (which) {
      case 'attach':
        this.showAttach = !this.showAttach;
        break;
      case 'foot':
        this.showFoot = !this.showFoot;
        break;
      case 'zoom':
        this.zoom2x = !this.zoom2x;
        this.page = 0;
        this.rebuildCells();
        break;
      case 'slow':
        this.slow = !this.slow;
        break;
      case 'step':
        this.stepMode = !this.stepMode;
        // Enter step mode on the frame currently shown by the reference (idle) cell.
        if (this.stepMode) this.stepIndex = this.cells[0]?.frame ?? 0;
        break;
      case 'aim':
        this.aimSweep = !this.aimSweep;
        if (!this.aimSweep) for (const c of this.cells) this.safe(c, () => c.view.setAim(c.facing === 1 ? 0 : Math.PI));
        break;
    }
    this.syncControls();
  }

  private stepFrames(d: number): void {
    if (!this.stepMode) {
      this.stepMode = true;
      this.stepIndex = this.cells[0]?.frame ?? 0;
    }
    this.stepIndex = Math.max(0, this.stepIndex + d);
    this.syncControls();
  }

  private flipPage(): void {
    if (!this.zoom2x) {
      this.zoom2x = true;
      this.page = 0;
    } else this.page = (this.page + 1) % 2;
    this.syncControls();
    this.rebuildCells();
  }

  /** Run a view call; on failure swap that cell to the raw-sheet fallback. */
  private safe(c: MechCell, fn: () => void): void {
    try {
      fn();
    } catch (err) {
      if (!c.view.real) return;
      if (!this.viewWarned) {
        console.warn('[preview] MechView failed, falling back to raw sheets', err);
        this.viewWarned = true;
      }
      c.view.destroy();
      c.view = new SheetView(this.scene, art.mechs[this.mech], this.mech, c.x, c.feetY, c.facing, c.scale);
      this.stage.add(c.view.root);
    }
  }

  private makeView(x: number, feetY: number, facing: 1 | -1, scale: number): CellView {
    try {
      const v = new RealView(createMechView(this.scene, this.mech), this.mech, x, feetY, facing, scale);
      v.setLoadout(this.loadout);
      return v;
    } catch (err) {
      if (!this.viewWarned) {
        console.warn('[preview] createMechView unavailable, showing raw sheet frames', err);
        this.viewWarned = true;
      }
      return new SheetView(this.scene, art.mechs[this.mech], this.mech, x, feetY, facing, scale);
    }
  }

  private rebuildCells(): void {
    for (const c of this.cells) c.view.destroy();
    this.cells = [];
    this.cards = [];
    this.stage.removeAll(true);
    for (const t of this.attachLabels) t.destroy();
    this.attachLabels = [];

    const s = this.scene;
    const scale = this.zoom2x ? 2 : 1;
    const anims = this.zoom2x ? ANIM_KEYS.slice(this.page * 4, this.page * 4 + 4) : ANIM_KEYS;
    const cols = this.zoom2x ? 2 : 4;
    const x0 = PV.sideW + PV.pad * 2;
    const y0 = PV.headerH + 62;
    const gap = 14;
    const cw = (PV.W - PV.pad - x0 - gap * (cols - 1)) / cols;
    const ch = (PV.H - PV.pad - y0 - gap) / 2;
    const mechArt = art.mechs[this.mech];
    const accent = MECHS[this.mech].reactorColor;
    const bg = s.add.graphics();
    this.stage.add(bg);

    anims.forEach((anim, i) => {
      const cx = x0 + (i % cols) * (cw + gap);
      const cy = y0 + Math.floor(i / cols) * (ch + gap);
      drawPanel(bg, cx, cy, cw, ch, { fill: COLORS.uiBg, fillAlpha: 0.75, chamfer: 10, accent });
      const info = mechArt?.anims[anim];
      const count = info?.count ?? ANIM_FRAMES[anim];
      const fps = info?.fps ?? 12;
      const loop = info?.loop ?? anim !== 'death';
      this.stage.add(label(s, cx + 14, cy + 10, anim.toUpperCase(), { font: 'display', size: 18, weight: 700, spacing: 3 }));
      this.stage.add(
        label(s, cx + cw - 14, cy + 14, `${count} F · ${fps} FPS · ${loop ? 'LOOP' : 'ONCE'}${anim === 'jump' || anim === 'dash' ? ' · PHASE' : ''}`, {
          font: 'mono',
          size: 11,
          color: COLORS.uiDim,
          originX: 1,
        }),
      );
      const feetY = cy + 52 + FOOT_ANCHOR.y * scale + 14;
      const frameText = label(s, cx + cw / 2, feetY + 10, '', { font: 'mono', size: 12, color: COLORS.uiDim, originX: 0.5 });
      this.stage.add(frameText);

      // Left-facing on the left, right-facing on the right: weapons point outward and never overlap.
      for (const facing of [-1, 1] as const) {
        const x = cx + cw * (facing === -1 ? 0.3 : 0.7);
        const view = this.makeView(x, feetY, facing, scale);
        this.stage.add(view.root);
        this.cells.push({ anim, facing, x, feetY, scale, view, frame: 0, lastLoop: -1 });
        this.stage.add(label(s, x, feetY + 30, facing === 1 ? 'RIGHT' : 'LEFT', { font: 'display', size: 10, color: COLORS.steel4, spacing: 3, originX: 0.5 }));
      }

      // Frame strip from the raw body sheet (QC: continuity, current frame highlighted).
      const thumbs: Card['thumbs'] = [];
      if (mechArt && s.textures.exists(mechArt.sheetKey) && info) {
        const perRow = this.zoom2x ? 12 : 6;
        const ts = this.zoom2x ? 0.42 : 0.38;
        const tw = CELL * ts + 4;
        const sx = cx + (cw - Math.min(count, perRow) * tw) / 2 + tw / 2;
        const sy = feetY + 58;
        for (let f = 0; f < count; f++) {
          const tx = sx + (f % perRow) * tw;
          const ty = sy + Math.floor(f / perRow) * (CELL * ts + 6) + (CELL * ts) / 2;
          const frame = info.start + f;
          if (!s.textures.get(mechArt.sheetKey).has(String(frame))) continue;
          const img = s.add.image(tx, ty, mechArt.sheetKey, frame).setScale(ts).setAlpha(0.75);
          this.stage.add(img);
          thumbs.push({ img, x: tx, y: ty, s: ts });
        }
      }
      this.cards.push({ anim, x: cx, y: cy, w: cw, h: ch, frameText, thumbs });
    });

    const realCount = this.cells.filter((c) => c.view.real).length;
    this.noteText.setText(
      realCount === this.cells.length
        ? `MECHVIEW · ${this.cells.length} VIEWS${this.zoom2x ? ` · PAGE ${this.page + 1}/2` : ''}`
        : mechArt
          ? 'MECHVIEW UNAVAILABLE · RAW SHEET FRAMES (NO EQUIPMENT)'
          : 'NO MECH ART REGISTERED · PLACEHOLDER SILHOUETTES',
    );
  }

  // ── per frame ──

  update(dt: number): void {
    if (!this.stepMode) this.clock += dt * (this.slow ? 0.25 : 1);
    const mechArt = art.mechs[this.mech];
    const viewDt = this.stepMode ? 0 : dt * (this.slow ? 0.25 : 1);
    const sweep = this.aimSweep ? Math.sin(this.clock * 1.4) * 0.7 - 0.15 : 0;

    for (const c of this.cells) {
      const info = mechArt?.anims[c.anim];
      const count = info?.count ?? ANIM_FRAMES[c.anim];
      const fps = info?.fps ?? 12;
      let frame: number;
      let loopIndex: number;
      if (this.stepMode) {
        frame = this.stepIndex % count;
        loopIndex = Math.floor(this.stepIndex / count);
      } else {
        const hold = HOLD_SEC[c.anim] ?? 0;
        const cycle = count / fps + hold;
        loopIndex = Math.floor(this.clock / cycle);
        frame = Math.min(count - 1, Math.floor((this.clock - loopIndex * cycle) * fps));
      }
      c.frame = frame;
      this.safe(c, () => {
        if (this.aimSweep) c.view.setAim(c.facing === 1 ? sweep : Math.PI - sweep);
        // The shoot cell pulses the upper-body recoil layer at the start of every loop.
        if (c.anim === 'shoot' && loopIndex !== c.lastLoop && frame === 0) c.view.recoil();
        c.view.show(c.anim, frame, count, viewDt);
      });
      c.lastLoop = loopIndex;
    }
    this.drawOverlay(mechArt);
  }

  private drawOverlay(mechArt: MechArt | undefined): void {
    const g = this.overlay;
    g.clear();
    const accent = MECHS[this.mech].reactorColor;
    let labelIdx = 0;

    for (const card of this.cards) {
      const ref = this.cells.find((c) => c.anim === card.anim);
      const frame = ref?.frame ?? 0;
      const count = mechArt?.anims[card.anim]?.count ?? ANIM_FRAMES[card.anim];
      card.frameText.setText(`FRAME ${String(frame + 1).padStart(2, '0')} / ${count}${this.stepMode ? '  · STEP' : this.slow ? '  · 0.25X' : ''}`);
      card.thumbs.forEach((t, i) => {
        const on = i === frame;
        t.img.setAlpha(on ? 1 : 0.55);
        if (on) {
          const half = (CELL * t.s) / 2 + 2;
          g.lineStyle(2, accent, 1);
          g.strokeRect(t.x - half, t.y - half, half * 2, half * 2);
        }
      });
    }

    for (const c of this.cells) {
      const s = c.scale;
      // Ground line under every cell (faint); bright foot-anchor marker when enabled.
      g.lineStyle(1, COLORS.steel3, 0.6);
      g.lineBetween(c.x - 76 * s, c.feetY, c.x + 76 * s, c.feetY);
      if (this.showFoot) {
        g.lineStyle(1, COLORS.telegraph, 0.95);
        g.lineBetween(c.x - 70 * s, c.feetY, c.x + 70 * s, c.feetY);
        g.lineStyle(1, COLORS.telegraph, 0.45);
        g.lineBetween(c.x, c.feetY - CELL * s, c.x, c.feetY + 6);
        // Cell bounds (128×128 around the anchor).
        const fa = mechArt?.footAnchor ?? FOOT_ANCHOR;
        const cell = mechArt?.cell ?? CELL;
        g.lineStyle(1, COLORS.steel4, 0.25);
        const left = c.facing === 1 ? c.x - fa.x * s : c.x - (cell - fa.x) * s;
        g.strokeRect(left, c.feetY - fa.y * s, cell * s, cell * s);
      }
      if (this.showAttach) {
        const meta = mechArt?.anims[c.anim]?.meta[c.frame];
        if (meta) labelIdx = this.drawAttach(g, c, meta, mechArt!, labelIdx);
      }
    }
    for (let i = labelIdx; i < this.attachLabels.length; i++) this.attachLabels[i].setVisible(false);
  }

  /** Dots + direction ticks + labels for one cell's current frame meta. Returns the next free label index. */
  private drawAttach(g: Phaser.GameObjects.Graphics, c: MechCell, meta: MechFrameMeta, a: MechArt, labelIdx: number): number {
    const s = c.scale;
    const fa = a.footAnchor;
    for (const key of ATTACH_KEYS) {
      const p = meta.attach[key];
      if (!p) continue;
      const x = c.x + (p.x - fa.x) * s * c.facing;
      const y = c.feetY + (p.y - fa.y) * s;
      // Mirrored rotation: angle θ facing right becomes π − θ facing left.
      const rot = c.facing === 1 ? p.rot : Math.PI - p.rot;
      const col = ATTACH_COLOR[key];
      g.lineStyle(1, col, 0.9);
      g.lineBetween(x, y, x + Math.cos(rot) * 9 * s, y + Math.sin(rot) * 9 * s);
      g.fillStyle(COLORS.ink, 1);
      g.fillCircle(x, y, 3 * Math.min(s, 1.5));
      g.fillStyle(col, 1);
      g.fillCircle(x, y, 2 * Math.min(s, 1.5));
      let t = this.attachLabels[labelIdx];
      if (!t) {
        t = label(this.scene, 0, 0, '', { font: 'mono', size: 9, weight: 500 });
        t.setStroke(css(COLORS.ink), 3);
        this.root.add(t);
        this.attachLabels.push(t);
      }
      t.setText(ATTACH_SHORT[key]).setColor(css(col)).setPosition(x + 4 * c.facing, y - 10).setOrigin(c.facing === 1 ? 0 : 1, 0).setVisible(true);
      labelIdx++;
    }
    return labelIdx;
  }

  key(code: string): boolean {
    switch (code) {
      case 'KeyQ':
        this.selectMech(MECH_IDS[(MECH_IDS.indexOf(this.mech) + MECH_IDS.length - 1) % MECH_IDS.length]);
        return true;
      case 'KeyE':
        this.selectMech(MECH_IDS[(MECH_IDS.indexOf(this.mech) + 1) % MECH_IDS.length]);
        return true;
      case 'KeyA':
        this.toggle('attach');
        return true;
      case 'KeyF':
        this.toggle('foot');
        return true;
      case 'KeyZ':
        this.toggle('zoom');
        return true;
      case 'KeyM':
        this.toggle('slow');
        return true;
      case 'KeyP':
      case 'Space':
        this.toggle('step');
        return true;
      case 'KeyG':
        this.toggle('aim');
        return true;
      case 'Comma':
      case 'ArrowLeft':
        this.stepFrames(-1);
        return true;
      case 'Period':
      case 'ArrowRight':
        this.stepFrames(1);
        return true;
      case 'BracketLeft':
      case 'BracketRight':
        this.flipPage();
        return true;
      case 'KeyW':
        this.weaponCycler.step(1);
        return true;
      case 'KeyR':
        this.applyPreset(4);
        return true;
      default:
        return false;
    }
  }

  destroy(): void {
    for (const c of this.cells) c.view.destroy();
    this.cells = [];
    this.root.destroy();
  }
}

function sameLoadout(a: Loadout, b: Loadout): boolean {
  return a.weapon === b.weapon && EQUIP_SLOTS.every((s) => a.equip[s] === b.equip[s]);
}
